import { FC } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

// Deep import on purpose: `@features/assessment` re-exports a *query* hook of
// the same name from assessment-documents-api, while the click-to-download
// mutation lives here. The run screen imports it the same way.
import { useGetDocumentDownloadUrl } from '@features/assessment/api/documents';
import { RemediationItemType } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { ArrowSquareOutIcon, FilesIcon, FileTextIcon } from '@phosphor-icons/react';

import { useRemediationRun } from '../api/remediation-api';

import { RemediationDialog } from './RemediationDialog';

/**
 * The documents a finished remediation produced, ready to open.
 *
 * Fetched when the dialog opens rather than with the table. The table asks for
 * runs without their items — for terminal runs that would be the tenant's whole
 * document history — so the one run the user actually pointed at is the only one
 * worth loading. `RemediationHistoryDialog` loads its runs the same way and for
 * the same reason.
 *
 * A `draftDocumentId` is what makes a document real here: it is set at finalize,
 * when the accepted content is rendered into a file. An item without one was
 * rejected, needed no changes, or failed — there is nothing to open, and listing
 * it would offer a button that cannot work.
 */
export const RemediatedDocumentsDialog: FC<{
  open: boolean;
  onClose: () => void;
  runId: string;
  assessmentTitle: string;
}> = observer(({ open, onClose, runId, assessmentTitle }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const { data: run, isLoading, isError } = useRemediationRun(open ? runId : undefined);
  const download = useGetDocumentDownloadUrl();

  const documents = (run?.items ?? []).filter((item) => item.draftDocumentId);

  return (
    <RemediationDialog
      open={open}
      onClose={onClose}
      title={t('app.remediation.produced.title')}
      subtitle={assessmentTitle}
      icon={<FilesIcon size={20} />}
      iconTone="success"
    >

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!isLoading && isError && (
          <Typography variant="textSM_regular" sx={{ color: 'error.main', display: 'block', py: 2 }}>
            {t('app.remediation.produced.loadError')}
          </Typography>
        )}

        {!isLoading && !isError && documents.length === 0 && (
          <Typography
            variant="textSM_regular"
            sx={{ color: 'text.secondary', display: 'block', py: 3, textAlign: 'center' }}
          >
            {t('app.remediation.produced.empty')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {documents.map((item) => (
            <Box
              key={item.itemId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              <FileTextIcon size={18} color={theme.palette.text.tertiary} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="textSM_regular"
                  sx={{ display: 'block', fontWeight: 500, overflowWrap: 'anywhere' }}
                >
                  {item.documentName ?? t('app.remediation.produced.untitled')}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={t(
                  item.type === RemediationItemType.GENERATE
                    ? 'app.remediation.produced.created'
                    : 'app.remediation.produced.improved',
                )}
                sx={{
                  flexShrink: 0,
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor:
                    item.type === RemediationItemType.GENERATE
                      ? theme.palette.ocean[100]
                      : theme.palette.blue[100],
                  color:
                    item.type === RemediationItemType.GENERATE
                      ? theme.palette.ocean[700]
                      : theme.palette.blue[700],
                }}
              />
              {/* One action, not a preview/download pair: the endpoint returns a
                  presigned URL opened in a new tab, which the browser previews
                  or saves depending on the file. Two buttons would be the same
                  request twice under different names. */}
              <Button
                variant="tertiary"
                startIcon={<ArrowSquareOutIcon size={15} />}
                disabled={download.isPending}
                onClick={() => download.mutate({ documentId: item.draftDocumentId as string })}
                sx={{ flexShrink: 0 }}
              >
                {t('app.remediation.produced.open')}
              </Button>
            </Box>
          ))}
        </Box>
    </RemediationDialog>
  );
});
